import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Checkbox, Drawer, DrawerItems, Label, Select, TextInput } from 'flowbite-react';
import { Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import CustomButton from 'src/components/common/custom-buttom';
import DataTablePresentation from 'src/components/tables/presentation';
import { AccessRight } from 'src/routes/PermissionEnum';
import { roleSchema } from 'src/schema';
import { useTMutation, useTQuery } from 'src/services/common';
const defaultValues = { name: '', description: '', status: 'active', isSuperAdmin: false };

const AddEditRole = ({ initialData, handleClose }: any) => {
  const [menuOption, setMenuOption] = useState<any[]>();
  const [permissionErr, setPermissionErr] = useState<boolean>(false);
  const { mutateAsync, isPending } = useTMutation({
    url: initialData?._id ? `role/${initialData?._id}` : 'role',
    method: initialData?._id ? 'patch' : 'post',
    invalidateTag: ['role'],
  });
  const { mutateAsync: deleteTemplate, isPending: deleteIsPending } = useTMutation({
    url: `role/${initialData?._id}`,
    method: 'delete',
    invalidateTag: ['role'],
  });

  const { data, isLoading } = useTQuery({ key: ['menu'], url: 'menu' });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: zodResolver(roleSchema),
  });

  const onSubmit = async (data: any) => {
    const permissions = menuOption.filter((val) => val?.isEdit == 1 || val?.isView == 1);
    if (permissions.length == 0) {
      setPermissionErr(true);
      return;
    } else setPermissionErr(false);

    let response: any = await mutateAsync({ ...data, permissions });
    if (response?.status) {
      toast.success(response?.message);
      onClose();
    } else {
      toast.error(response?.data?.message);
    }
  };
  const onDelete = async () => {
    let response: any = await deleteTemplate({});
    if (response?.status) {
      toast.success(response?.message);
      onClose();
    } else {
      toast.error(response?.data?.message);
    }
  };
  const onClose = () => {
    handleClose(false);
    reset(defaultValues);
  };

  const columns = [
    {
      header: 'Name',
      accessor: 'name',
    },
    {
      header: 'View',
      accessor: 'isView',
      cell: (row, index) => (
        <Checkbox
          checked={row?.isView == 1}
          onChange={(e) => {
            const updated = [...menuOption];
            updated[index].isView = e.target.checked ? 1 : 0;
            if (!e.target.checked) {
              updated[index].isEdit = 0;
            }
            setMenuOption(updated);
          }}
        />
      ),
    },
    {
      header: 'Edit',
      accessor: 'isEdit',
      cell: (row, index) => (
        <Checkbox
          checked={row?.isEdit == 1}
          onChange={(e) => {
            const updated = [...menuOption];
            updated[index].isEdit = e.target.checked ? 1 : 0;
            if (e.target.checked) {
              updated[index].isView = 1;
            }
            setMenuOption(updated);
          }}
        />
      ),
    },
  ];

  const mergeMenuOption = () => {
    const existingPermissions = initialData?.permissions || [];
    const allMenus = data?.data || [];

    const combined = allMenus.map((menu) => {
      const existing = existingPermissions.find((p) => p.menuID === menu._id);
      return {
        menuID: menu._id,
        name: menu.menuLabel,
        isView: existing?.isView ?? 0,
        isEdit: existing?.isEdit ?? 0,
      };
    });

    setMenuOption(combined);
  };

  useEffect(() => {
    if (data?.data) mergeMenuOption();
  }, [data?.data, initialData]);

  useEffect(() => {
    if (initialData?._id) {
      reset(initialData);
      mergeMenuOption();
    }
  }, [initialData, reset]);

  return (
    <>
      <Drawer className={`p-4 w-[60%]`} open={initialData} onClose={onClose} position="right">
        <div className="flex flex-1 justify-between cursor-pointer">
          <Label className="text-[22px]" value={initialData?._id ? 'Edit Role' : 'Add Role'} />
          <X className="rounded-full bg-red-200 p-1 cursor-pointer" onClick={onClose} />
        </div>

        <DrawerItems className="mt-5">
          <form onSubmit={handleSubmit(onSubmit)} className="w-full">
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="mb-2 block">
                    <Label htmlFor="name" value="Name" />
                  </div>
                  <TextInput
                    id="name"
                    type="text"
                    sizing="md"
                    className="form-control form-rounded-xl"
                    {...register('name')}
                  />
                  {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
                </div>
                <div className="flex-1">
                  <div className="mb-2 block">
                    <Label htmlFor="status" value="Status" />
                  </div>
                  <Select
                    {...register('status')}
                    id="status"
                    required
                    className="focus:ring-0 border-gray-300"
                    style={{ borderRadius: '10px' }} // <-- removes border-radius
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>

                  {errors.status && <p className="text-red-500 text-sm">{errors.status.message}</p>}
                </div>
              </div>

              <div className="flex-1">
                <div className="mb-2 block">
                  <Label htmlFor="description" value="Description" />
                </div>
                <TextInput
                  id="description"
                  type="text"
                  sizing="md"
                  className="form-control form-rounded-xl"
                  {...register('description')}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm">{errors.description.message}</p>
                )}

                <div className="flex items-center gap-2 mt-4">
                  <Checkbox id="isSuperAdmin" {...register('isSuperAdmin')} />
                  <Label htmlFor="isSuperAdmin" value="Super admin (can view users' private galleries and flag accounts)" />
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-2 block">
                  <Label htmlFor="permission" value="Permissions" />
                  <div className="mt-2">
                    <DataTablePresentation
                      columns={columns}
                      response={{ data: menuOption }}
                      loading={isLoading}
                    />
                    {permissionErr && (
                      <p className="text-red-500 text-sm">Select at least one permission </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                {initialData?._id && (
                  <CustomButton
                    accessRight={AccessRight.ISEDIT}
                    onClick={() => onDelete()}
                    className="bg-red-500 text-white rounded-xl"
                  >
                    {deleteIsPending ? <Loader2 className="animate-spin" /> : 'Delete'}
                  </CustomButton>
                )}

                <Button onClick={onClose} color="light" className=" rounded-xl">
                  Cancel
                </Button>
                <CustomButton
                  accessRight={AccessRight.ISEDIT}
                  type="submit"
                  color="green"
                  className="bg-green-400 text-white rounded-xl"
                >
                  {isPending ? <Loader2 className="animate-spin" /> : 'Save'}
                </CustomButton>
              </div>
            </div>
          </form>
        </DrawerItems>
      </Drawer>
    </>
  );
};
export default AddEditRole;
